import { FC } from 'react';
import BiotechIcon from '@mui/icons-material/Biotech';
import { Box, Typography } from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { VirtualizedAutocomplete } from 'src/components/virtualized_autocomplete';
import { PathologyLabFieldsFragment, PathologyLabType } from 'src/types';

interface PathologyLabAutocompleteProps {
  orderRepository: LdtOrderRepository;
  value?: PathologyLabFieldsFragment | null; // Selected pathology lab
  onChange: (pathologyLab: PathologyLabFieldsFragment | null) => void;
  label?: string;
}

export const PathologyLabAutocomplete: FC<PathologyLabAutocompleteProps> = ({
  orderRepository,
  value,
  onChange,
  label = 'Pathology Lab',
}) => {
  // Format pathology lab display name
  const getLabLabel = (lab: PathologyLabFieldsFragment | null): string => {
    if (!lab) return '';
    return lab.name;
  };

  // Adapter to convert cursor-based response to CursorPaginatedResponse
  const fetchLabs = async (params: {
    searchText?: string;
    cursor?: string | null;
    first: number;
  }) => {
    const result = await orderRepository.getPathologyLabs({
      searchText: params.searchText,
      cursor: params.cursor,
      first: params.first,
    });

    return {
      items: result.items || [],
      nextCursor: result.nextCursor,
      hasNextPage: result.hasNextPage,
    };
  };

  return (
    <VirtualizedAutocomplete<PathologyLabFieldsFragment>
      queryKey={['pathologyLabs']}
      fetchPage={fetchLabs}
      value={value}
      onChange={onChange}
      getItemId={(lab) => lab.id}
      getItemLabel={getLabLabel}
      label={label}
      placeholder='Search pathology lab by Name'
      renderOption={(lab) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {lab.type === PathologyLabType.DirectUpload && (
            <BiotechIcon
              fontSize='small'
              color='action'
              sx={{ mr: 1, flexShrink: 0 }}
            />
          )}
          <Typography variant='body2' noWrap>
            {getLabLabel(lab)}
          </Typography>
        </Box>
      )}
      renderNoOptions={(searchText, isLoading) => {
        if (isLoading) return 'Loading...';
        if (searchText.trim()) {
          return 'No lab found. Check spelling or ensure the lab is first created in Salesforce.';
        }
        return `Type to search ${label.toLowerCase()}`;
      }}
    />
  );
};
